import React, { useMemo, useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  TrophyOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  BadgeDto,
  BadgeKind,
  CreateBadgeInput,
  useBadges,
  useBadgeUnlocks,
  useCreateBadge,
  useDeleteBadge,
  useGrantBadge,
  useRevokeBadge,
  useUpdateBadge,
} from '@shared/api/hooks/useBadges';
import { useUsers } from '@shared/api/hooks/useUsers';

const KIND_LABEL: Record<BadgeKind, string> = {
  MANUAL: 'Manuale (admin)',
  POINTS_THRESHOLD: 'Auto: soglia punti',
  RECEIPTS_COUNT: 'Auto: scontrini caricati',
  CHECKINS_COUNT: 'Auto: check-in stadio',
  ORDERS_COUNT: 'Auto: ordini shop',
  CUSTOM: 'Custom (regola applicativa)',
};

const KIND_COLOR: Record<BadgeKind, string> = {
  MANUAL: 'default',
  POINTS_THRESHOLD: 'gold',
  RECEIPTS_COUNT: 'green',
  CHECKINS_COUNT: 'blue',
  ORDERS_COUNT: 'orange',
  CUSTOM: 'purple',
};

type BadgeFormValues = CreateBadgeInput;

export function BadgesPage(): React.ReactElement {
  const list = useBadges();
  const createMut = useCreateBadge();
  const updateMut = useUpdateBadge();
  const deleteMut = useDeleteBadge();

  const [editing, setEditing] = useState<BadgeDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [drillBadge, setDrillBadge] = useState<BadgeDto | null>(null);

  const [form] = Form.useForm<BadgeFormValues>();
  const kind = Form.useWatch('kind', form);
  const isThresholdKind =
    kind && kind !== 'MANUAL' && kind !== 'CUSTOM';

  const openCreate = (): void => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      kind: 'MANUAL',
      sortOrder: list.data?.length ?? 0,
      isActive: true,
    });
    setCreateOpen(true);
  };

  const openEdit = (b: BadgeDto): void => {
    setEditing(b);
    form.setFieldsValue({
      code: b.code,
      name: b.name,
      description: b.description ?? undefined,
      icon: b.icon,
      kind: b.kind,
      threshold: b.threshold ?? undefined,
      sortOrder: b.sortOrder,
      isActive: b.isActive,
    });
    setCreateOpen(true);
  };

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    const payload: CreateBadgeInput = {
      ...values,
      threshold:
        values.kind && values.kind !== 'MANUAL' && values.kind !== 'CUSTOM'
          ? values.threshold
          : undefined,
    };
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, input: payload });
        message.success('Badge aggiornato');
      } else {
        await createMut.mutateAsync(payload);
        message.success('Badge creato');
      }
      setCreateOpen(false);
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Operazione fallita',
      );
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMut.mutateAsync(id);
      message.success('Badge eliminato');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Errore');
    }
  };

  const columns = [
    {
      title: 'Icona',
      dataIndex: 'icon',
      width: 70,
      render: (v: string) => <span style={{ fontSize: 22 }}>{v}</span>,
    },
    {
      title: 'Codice',
      dataIndex: 'code',
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      render: (v: string, b: BadgeDto) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v}</div>
          {b.description && (
            <div style={{ fontSize: 12, color: '#888' }}>{b.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'kind',
      render: (k: BadgeKind, b: BadgeDto) => (
        <Space direction="vertical" size={0}>
          <Tag color={KIND_COLOR[k]}>{KIND_LABEL[k]}</Tag>
          {b.threshold != null && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              soglia: {b.threshold}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Sblocchi',
      dataIndex: 'unlockedCount',
      width: 110,
      render: (n: number | undefined, b: BadgeDto) => (
        <Button
          type="link"
          size="small"
          onClick={() => setDrillBadge(b)}
          disabled={!n}
        >
          {n ?? 0} tifosi
        </Button>
      ),
    },
    {
      title: 'Stato',
      dataIndex: 'isActive',
      width: 100,
      render: (v: boolean) =>
        v ? <Tag color="green">Attivo</Tag> : <Tag>Disattivato</Tag>,
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 200,
      render: (_: unknown, b: BadgeDto) => (
        <Space>
          <Button
            size="small"
            icon={<UserAddOutlined />}
            onClick={() => setDrillBadge(b)}
          >
            Assegna
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(b)}
          />
          <Popconfirm
            title="Eliminare il badge?"
            description="Rimuove anche tutti gli sblocchi dei tifosi."
            okText="Elimina"
            okButtonProps={{ danger: true }}
            cancelText="Annulla"
            onConfirm={() => onDelete(b.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <TrophyOutlined />
            <span>Trofei e badge</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              Nuovo badge
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary">
          Catalogo trofei dell&apos;app. Manuali = forzabili sul singolo tifoso.
          Auto* = sblocco automatico futuro tramite cron/event listeners.
        </Typography.Paragraph>
        <Table
          rowKey="id"
          loading={list.isLoading}
          columns={columns}
          dataSource={list.data ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editing ? `Modifica badge · ${editing.code}` : 'Nuovo badge'}
        open={createOpen}
        onOk={submit}
        confirmLoading={createMut.isPending || updateMut.isPending}
        onCancel={() => setCreateOpen(false)}
        okText={editing ? 'Salva' : 'Crea'}
        cancelText="Annulla"
        width={520}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            label="Codice univoco"
            name="code"
            rules={[
              { required: true },
              {
                pattern: /^[A-Z0-9_]{2,50}$/,
                message: 'UPPER_SNAKE_CASE (lett/cifre/_)',
              },
            ]}
          >
            <Input placeholder="DERBY_WARRIOR" disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item
            label="Nome visualizzato"
            name="name"
            rules={[{ required: true, max: 120 }]}
          >
            <Input placeholder="Derby warrior" />
          </Form.Item>
          <Form.Item label="Descrizione" name="description">
            <Input placeholder="5 derby presenti" maxLength={500} />
          </Form.Item>
          <Form.Item
            label="Icona (emoji)"
            name="icon"
            rules={[{ required: true, max: 20 }]}
          >
            <Input placeholder="🔥" />
          </Form.Item>
          <Form.Item
            label="Tipo"
            name="kind"
            rules={[{ required: true }]}
            initialValue="MANUAL"
          >
            <Select
              options={(Object.keys(KIND_LABEL) as BadgeKind[]).map((k) => ({
                label: KIND_LABEL[k],
                value: k,
              }))}
            />
          </Form.Item>
          {isThresholdKind && (
            <Form.Item
              label="Soglia"
              name="threshold"
              rules={[{ required: true, type: 'number', min: 1 }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item label="Ordine visualizzazione" name="sortOrder" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Attivo" name="isActive" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <UnlocksDrawer badge={drillBadge} onClose={() => setDrillBadge(null)} />
    </div>
  );
}

interface UnlocksDrawerProps {
  badge: BadgeDto | null;
  onClose: () => void;
}

function UnlocksDrawer({ badge, onClose }: UnlocksDrawerProps): React.ReactElement {
  const unlocks = useBadgeUnlocks(badge?.id ?? null);
  const grantMut = useGrantBadge();
  const revokeMut = useRevokeBadge();

  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const usersQuery = useUsers({
    role: 'FAN',
    limit: 20,
    search: search.trim() || undefined,
  });

  const options = useMemo(
    () =>
      (usersQuery.data?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.displayName ?? u.email} · ${u.email}`,
      })),
    [usersQuery.data],
  );

  const grant = async (): Promise<void> => {
    if (!badge || !selectedUserId) return;
    try {
      await grantMut.mutateAsync({
        badgeId: badge.id,
        userId: selectedUserId,
        reason: reason.trim() || undefined,
      });
      message.success('Badge assegnato');
      setSelectedUserId(null);
      setReason('');
      setSearch('');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Errore');
    }
  };

  const revoke = async (userId: string): Promise<void> => {
    if (!badge) return;
    try {
      await revokeMut.mutateAsync({ badgeId: badge.id, userId });
      message.success('Badge revocato');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Errore');
    }
  };

  const cols = [
    { title: 'Tifoso', dataIndex: 'displayName', render: (v: string | null) => v ?? '—' },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Fan card', dataIndex: 'fanCardCode' },
    {
      title: 'Sbloccato',
      dataIndex: 'unlockedAt',
      render: (v: string) => new Date(v).toLocaleString('it-IT'),
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: { userId: string }) => (
        <Popconfirm
          title="Revocare badge a questo tifoso?"
          okText="Revoca"
          okButtonProps={{ danger: true }}
          onConfirm={() => revoke(r.userId)}
        >
          <Button size="small" danger>
            Revoca
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      title={
        badge ? (
          <Space>
            <span style={{ fontSize: 22 }}>{badge.icon}</span>
            <span>{badge.name}</span>
          </Space>
        ) : (
          'Sblocchi'
        )
      }
      width={760}
      open={Boolean(badge)}
      onClose={onClose}
    >
      <Card title="Forza assegnazione" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <AutoComplete
            value={search}
            onChange={(v) => {
              setSearch(v);
              setSelectedUserId(null);
            }}
            onSelect={(v) => {
              setSelectedUserId(v as string);
              const opt = options.find((o) => o.value === v);
              setSearch(opt?.label ?? '');
            }}
            options={options}
            style={{ width: '100%' }}
            placeholder="Cerca tifoso per email o nome…"
            allowClear
          />
          <Input
            placeholder="Motivazione (opzionale, finisce in audit log)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            disabled={!selectedUserId}
            loading={grantMut.isPending}
            onClick={grant}
          >
            Assegna badge
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="userId"
        loading={unlocks.isLoading}
        columns={cols}
        dataSource={unlocks.data ?? []}
        pagination={{ pageSize: 20 }}
        size="small"
      />
    </Drawer>
  );
}
